<script setup lang="ts">
import {
  Trash2 as Trash2Icon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  Route as RouteIcon,
  Edit as EditIcon,
  Plus as PlusIcon,
} from '@lucide/vue'
import { computed, ref } from 'vue'

import { showConfirm } from '@/services/dialogService'
import { requirePermission } from '@/services/permissionService'
import { TrackColorService } from '@/services/trackColorService'
import { calculateTrackDistance } from '@/services/trackMetricsCalculationService'
import { isScreenSizeBelow, ScreenSize } from '@/services/uiService'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Track, Tour } from '@/types/tour'
import { requireItemEditable } from '@/utils/permissionDialog'
import { getEffectiveTourName } from '@/utils/tourName'

import TrackEditModal from '../modals/TrackEditModal.vue'

const props = withDefaults(
  defineProps<{
    tour?: Tour
    expanded?: boolean
    allowAddTrack?: boolean
  }>(),
  {
    allowAddTrack: true,
  },
)

const workspaceStore = useWorkspaceStore()
const emit = defineEmits<{
  'zoom-track': [tourId: string, trackIndex: number]
  'update:expanded': [value: boolean]
  'select-tour': [tourId: string]
}>()

const isExpanded = ref(
  props.expanded !== undefined ? props.expanded : !isScreenSizeBelow(ScreenSize.MD),
)

const handleAddTrack = async () => {
  if (!(await requirePermission('edit_track'))) return
  window.dispatchEvent(new CustomEvent('start-track-drawing'))
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  emit('update:expanded', isExpanded.value)
}

const editModalOpen = ref(false)
const editingTourId = ref('')
const editingTrackIndex = ref(-1)

const tracks = computed(() => {
  if (props.tour) {
    // If tour prop is provided, only show tracks from this specific tour
    return props.tour.tracks.map((track, index) => {
      const trackColor = track.color
      return {
        track,
        tourId: props.tour!.clientId,
        index,
        tourName: getEffectiveTourName(props.tour!),
        color: TrackColorService.getTourColor(props.tour!.clientId, trackColor),
      }
    })
  } else {
    // Fallback to previous behavior (all tracks from active tours)
    const tracksWithInfo: {
      track: Track
      tourId: string
      index: number
      tourName: string
      color: string
    }[] = []

    workspaceStore.tours.forEach((tour) => {
      if (tour.active) {
        tour.tracks.forEach((track, index) => {
          const trackColor = track.color
          tracksWithInfo.push({
            track,
            tourId: tour.clientId,
            index,
            tourName: getEffectiveTourName(tour),
            color: TrackColorService.getTourColor(tour.clientId, trackColor),
          })
        })
      }
    })

    return tracksWithInfo
  }
})

const handleTrackClick = (tourId: string, trackIndex: number) => {
  emit('zoom-track', tourId, trackIndex)
}

const openEditModal = (tourId: string, trackIndex: number, event: Event) => {
  event.stopPropagation()
  editingTourId.value = tourId
  editingTrackIndex.value = trackIndex
  editModalOpen.value = true
}

const handleEditSave = async (
  tourId: string,
  trackIndex: number,
  updates: { name: string; color: string },
) => {
  if (!(await requirePermission('edit_track'))) return
  const tour = workspaceStore.tours.find((t) => t.clientId === tourId)
  if (tour && trackIndex >= 0 && trackIndex < tour.tracks.length) {
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return
    const trackId = tour.tracks[trackIndex].clientId
    workspaceStore.updateTrack(tourId, trackIndex, {
      ...updates,
      clientId: trackId,
    })
  }
}

const handleTrackDelete = async (tourId: string, trackIndex: number) => {
  if (!(await requirePermission('delete_track'))) return
  const tour = workspaceStore.tours.find((t) => t.clientId === tourId)
  if (!tour || trackIndex < 0 || trackIndex >= tour.tracks.length) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

  const track = tour.tracks[trackIndex]
  const trackName = track.name || `Track ${trackIndex + 1}`

  const confirmed = await showConfirm({
    title: 'Delete Track',
    message: `Are you sure you want to delete "${trackName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    await workspaceStore.deleteTrack(tourId, trackIndex)
  }
}

const toggleTrackVisibility = (tourId: string, trackIndex: number, event: Event) => {
  event.stopPropagation()
  const track = props.tour
    ? props.tour.tracks[trackIndex]
    : workspaceStore.tours.find((t) => t.clientId === tourId)?.tracks[trackIndex]

  if (track) {
    const newVisibility = track.visible === undefined ? false : !track.visible
    workspaceStore.setTrackVisibility(tourId, trackIndex, newVisibility)
  }
}

const toggleAllTracksVisibility = (visible: boolean) => {
  const tourId = props.tour?.clientId
  if (tourId) {
    workspaceStore.toggleAllTracksVisibility(tourId, visible)
  } else {
    tracks.value.forEach(({ tourId }) => {
      workspaceStore.toggleAllTracksVisibility(tourId, visible)
    })
  }
}

const isAnyTrackVisible = computed(() => {
  return tracks.value.some(({ tourId, index }) => isTrackVisible(tourId, index))
})

const formatDistance = (meters: number): string => {
  return (meters / 1000).toFixed(1) + ' km'
}

const deleteTrack = async (tourId: string, trackIndex: number, event: Event) => {
  event.stopPropagation()
  if (!(await requirePermission('delete_track'))) return

  const tour = workspaceStore.tours.find((t) => t.clientId === tourId)
  if (!tour || trackIndex < 0 || trackIndex >= tour.tracks.length) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

  const track = tour.tracks[trackIndex]
  const trackName = track.name || `Track ${trackIndex + 1}`

  const confirmed = await showConfirm({
    title: 'Delete Track',
    message: `Are you sure you want to delete "${trackName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    workspaceStore.deleteTrack(tourId, trackIndex)
  }
}

const deleteAllTracks = async () => {
  if (!(await requirePermission('delete_track'))) return
  const trackCount = tracks.value.length
  if (trackCount === 0) return
  // Check creator-only content edit for every distinct tour involved *before* the "are you
  // sure" prompt, so a non-creator sees only the explainer instead of a confirm they can't
  // actually act on.
  const involvedTourIds = new Set(tracks.value.map(({ tourId }) => tourId))
  for (const tourId of involvedTourIds) {
    const tour =
      props.tour?.clientId === tourId
        ? props.tour
        : workspaceStore.tours.find((t) => t.clientId === tourId)
    if (tour && !(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return
  }

  const confirmed = await showConfirm({
    title: 'Delete All Tracks',
    message: `Are you sure you want to delete all ${trackCount} track${trackCount > 1 ? 's' : ''}? This action cannot be undone.`,
    confirmText: 'Delete All',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  if (props.tour) {
    // Delete all tracks from the specific tour
    const indices = Array.from({ length: props.tour.tracks.length }, (_, i) => i)

    // Sort indices in descending order to avoid index shifting issues
    indices.sort((a, b) => b - a)

    indices.forEach((index) => {
      workspaceStore.deleteTrack(props.tour!.clientId, index)
    })
  } else {
    const tracksToDelete = new Map<string, number[]>()

    tracks.value.forEach(({ tourId, index }) => {
      if (!tracksToDelete.has(tourId)) {
        tracksToDelete.set(tourId, [])
      }
      tracksToDelete.get(tourId)?.push(index)
    })

    tracksToDelete.forEach((indices, tourId) => {
      indices.sort((a, b) => b - a)
      indices.forEach((index) => {
        workspaceStore.deleteTrack(tourId, index)
      })
    })
  }
}

const isTrackVisible = (tourId: string, trackIndex: number) => {
  if (props.tour && props.tour.clientId === tourId) {
    const track = props.tour.tracks[trackIndex]
    return track.visible !== undefined ? track.visible : props.tour.visible
  }

  const tour = workspaceStore.tours.find((t) => t.clientId === tourId)
  if (!tour) return false

  const track = tour.tracks[trackIndex]
  return track.visible !== undefined ? track.visible : tour.visible
}
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Track header - sticky -->
    <div
      :class="[
        'sticky',
        'top-0',
        'z-10',
        'flex',
        'justify-between',
        'items-center',
        'p-2',
        'shadow-sm',
        'flex-shrink-0',
        'h-10',
        'group',
        isExpanded ? 'bg-gray-100' : 'bg-gray-50',
      ]"
    >
      <button
        @click="toggleExpanded"
        class="flex items-center flex-1 hover:text-primary-600 focus:outline-none hover:cursor-pointer"
      >
        <div class="flex items-center">
          <ChevronDownIcon v-if="isExpanded" class="w-4 h-4 text-gray-500" />
          <ChevronRightIcon v-else class="w-4 h-4 text-gray-500" />
          <RouteIcon class="w-4 h-4 ml-1 mr-1.5 text-primary-500" />
          <span class="text-sm font-medium">Tracks ({{ tracks.length }})</span>
        </div>
      </button>
      <div class="flex items-center space-x-1">
        <!-- Add track button -->
        <button
          v-if="props.allowAddTrack"
          @click="handleAddTrack"
          class="p-1 text-primary-600 hover:bg-primary-50 rounded-md transition-colors hover:text-primary-700 hover:cursor-pointer"
          title="Add track"
        >
          <PlusIcon class="w-4 h-4" />
        </button>

        <!-- Eye toggle button -->
        <button
          @click="toggleAllTracksVisibility(!isAnyTrackVisible)"
          class="p-1 text-gray-500 hover:text-gray-700 hover:cursor-pointer"
          :title="isAnyTrackVisible ? 'Hide all tracks' : 'Show all tracks'"
        >
          <component
            v-if="tracks.length"
            :is="isAnyTrackVisible ? EyeIcon : EyeOffIcon"
            class="w-4 h-4"
          />
        </button>

        <!-- Clear all tracks button -->
        <button
          v-if="tracks.length > 0"
          @click="deleteAllTracks"
          class="p-1 text-red-500 hover:bg-red-50 rounded-md transition-colors hover:text-red-700 hover:cursor-pointer"
          title="Delete all tracks"
        >
          <Trash2Icon class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Track content - only rendered when expanded -->
    <div v-if="isExpanded" class="overflow-y-auto flex-1 bg-white">
      <ul class="divide-y divide-gray-100">
        <li
          v-for="(item, i) in tracks"
          :key="`${item.tourId}-${item.index}`"
          class="py-2.5 px-3 transition-colors duration-200 hover:bg-gray-50 relative cursor-pointer group"
          @click="handleTrackClick(item.tourId, item.index)"
        >
          <div class="flex justify-between items-center">
            <div class="flex items-center">
              <div
                class="w-2.5 h-2.5 rounded-full mr-2"
                :style="{ backgroundColor: item.color }"
              ></div>
              <span
                class="text-sm truncate max-w-32"
                :title="item.track.name || `Track ${i + 1} (${item.tourName})`"
                :class="{ 'text-gray-400': !isTrackVisible(item.tourId, item.index) }"
              >
                {{ item.track.name || `Track ${i + 1}` }}
              </span>
            </div>

            <div
              class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <!-- Toggle visibility button -->
              <button
                @click.stop="toggleTrackVisibility(item.tourId, item.index, $event)"
                class="p-1 text-gray-500 hover:text-gray-700 hover:cursor-pointer"
                :title="isTrackVisible(item.tourId, item.index) ? 'Hide track' : 'Show track'"
              >
                <component
                  :is="isTrackVisible(item.tourId, item.index) ? EyeIcon : EyeOffIcon"
                  class="w-4 h-4"
                />
              </button>

              <!-- Edit button -->
              <button
                @click.stop="openEditModal(item.tourId, item.index, $event)"
                class="p-1 text-primary-500 hover:text-primary-700 hover:cursor-pointer"
                title="Edit track"
              >
                <EditIcon class="w-4 h-4" />
              </button>

              <!-- Delete button -->
              <button
                @click.stop="deleteTrack(item.tourId, item.index, $event)"
                class="p-1 text-red-500 hover:text-red-700 hover:cursor-pointer"
                title="Delete track"
              >
                <Trash2Icon class="w-4 h-4" />
              </button>
            </div>
          </div>

          <div class="mt-1 text-xs text-gray-500">
            <span>{{ formatDistance(calculateTrackDistance(item.track)) }}</span>
          </div>

          <div class="mt-1 text-xs text-gray-500">
            <span>Tour: {{ item.tourName }}</span>
          </div>
        </li>
      </ul>

      <div
        v-if="tracks.length === 0 && !isScreenSizeBelow(ScreenSize.MD)"
        class="p-4 text-center text-gray-500"
      >
        <p>No tracks available.</p>
      </div>
    </div>

    <!-- Track Edit Modal -->
    <TrackEditModal
      :is-open="editModalOpen"
      :tour-id="editingTourId"
      :track-index="editingTrackIndex"
      @close="editModalOpen = false"
      @save="handleEditSave"
      @delete-track="handleTrackDelete"
    />
  </div>
</template>
